import { ActionButton } from '@/components/common/ActionButton';
import { MOTION } from '@/components/common/motion';
import { SelectionCheckbox } from '@/components/common/SelectionCheckbox';
import { SECTION_LABEL } from '@/components/common/surfaces';
import type { ColumnDef, ColumnKey } from '@/components/tools/toolColumns';
import {
  COLUMN_GROUP_LABEL_KEYS,
  COLUMN_GROUPS,
  COLUMN_PRESETS,
  COLUMNS,
  matchesPreset,
  PINNED_COLUMN,
} from '@/components/tools/toolColumns';
import { ArrowCounterClockwise, Lock } from '@phosphor-icons/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A preset chip.
 *
 * Written out rather than composed from `CHIP`, because the active state swaps both the
 * background and the text colour: appending `bg-gray-900 text-white` to a recipe that already
 * says `bg-gray-100 text-gray-700` puts two utilities for the same property on one element, and
 * which of them wins comes down to the order Tailwind emitted them in, not to this file.
 */
const PRESET_CHIP_BASE = `rounded-full px-3 py-1 text-xs font-medium ${MOTION} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2`;
const PRESET_CHIP_IDLE = `${PRESET_CHIP_BASE} bg-gray-100 text-gray-700 hover:bg-gray-200`;
const PRESET_CHIP_ACTIVE = `${PRESET_CHIP_BASE} bg-gray-900 text-white`;

interface ColumnChooserProps {
  /** Keys currently in the table, in no particular order. */
  visibleKeys: ColumnKey[];
  /** One column switched on or off. The pinned column never reaches this. */
  onToggle: (key: ColumnKey, visible: boolean) => void;
  /** A whole preset picked. */
  onApplyPreset: (keys: ColumnKey[]) => void;
  /** Visible set, order and widths all back to the defaults. */
  onReset: () => void;
}

/**
 * The contents of the "Sloupce" overlay: what the table may show, and what it does show.
 *
 * The catalogue carries twenty-two columns and the table has always shown ten of them, which
 * meant the other twelve were invisible in the strict sense — there was nothing on the page to
 * suggest they existed. The chooser's job is as much to *advertise* them as to toggle them,
 * which is why it leads with presets: a user who does not know what `Úhel boku` is still
 * understands "Geometrie".
 *
 * The tool-name row is rendered locked rather than omitted. A missing row would read as "this
 * column does not exist"; a checked, disabled one with a padlock reads as "this one stays".
 */
export const ColumnChooser: FC<ColumnChooserProps> = ({ visibleKeys, onToggle, onApplyPreset, onReset }) => {
  const { t } = useTranslation();
  const visible = new Set<string>(visibleKeys);

  return (
    <div className="flex w-80 max-w-[80vw] flex-col">
      <div className="flex flex-wrap gap-2 border-b border-gray-200 px-4 py-3">
        {COLUMN_PRESETS.map((preset) => {
          const active = matchesPreset(visibleKeys, preset);
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onApplyPreset(preset.keys)}
              aria-pressed={active}
              className={active ? PRESET_CHIP_ACTIVE : PRESET_CHIP_IDLE}
            >
              {t(preset.labelKey)}
            </button>
          );
        })}
      </div>

      {/* 320 px rather than a viewport fraction: twenty-two rows are 600 px of list, and a
          panel taller than the room under its button gets flipped above it and clipped by
          the page header. A fixed, modest scroll window keeps the whole panel on screen at
          every height this page is used at. */}
      <div className="flex max-h-80 flex-col gap-4 overflow-y-auto px-4 py-3">
        {COLUMN_GROUPS.map((group) => {
          const columns: ColumnDef[] = COLUMNS.filter((c) => c.group === group);
          if (columns.length === 0) return null;
          return (
            <div key={group} className="flex flex-col gap-1">
              <span className={SECTION_LABEL}>{t(COLUMN_GROUP_LABEL_KEYS[group])}</span>
              {columns.map((col) => {
                const locked = col.key === PINNED_COLUMN;
                const checked = locked || visible.has(col.key);
                return (
                  // The label is the hit target, so the whole row toggles rather than only the
                  // 20 px box; `has-[:disabled]` drops the hover on the row that cannot move.
                  <label
                    key={col.key}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 hover:bg-gray-50 has-[:disabled]:cursor-default has-[:disabled]:hover:bg-transparent ${MOTION}`}
                  >
                    <SelectionCheckbox
                      checked={checked}
                      disabled={locked}
                      onChange={(next) => onToggle(col.key, next)}
                      aria-label={t(col.labelKey)}
                    />
                    <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{t(col.labelKey)}</span>
                    {col.unit ? <span className="shrink-0 text-xs text-gray-400">{col.unit}</span> : null}
                    {locked ? (
                      <span className="flex shrink-0 items-center gap-1 text-xs text-gray-400">
                        <Lock size={12} aria-hidden="true" />
                        {t('toolManagement.columnsAlwaysShown')}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="border-t border-gray-200 px-4 py-2">
        <ActionButton
          variant="ghost"
          icon={<ArrowCounterClockwise size={16} aria-hidden="true" />}
          onClick={onReset}
        >
          {t('toolManagement.columnsReset')}
        </ActionButton>
      </div>
    </div>
  );
};
